import { useQuery } from "@apollo/client";
import { Combobox } from "@/components/Combobox";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";

type Props = {
  id?: string;
  selected: string[];
  onSelect: (values: string[]) => void;
  className?: string;
};

export type DestinationCityData = ResultOf<typeof destinationCitiesQuery>;
type CityNode = NonNullable<
  NonNullable<NonNullable<DestinationCityData>["supplyChainLotDestinationCities"]>["edges"][number]
>["node"];

export function DestinationCityPicker({ id, selected, onSelect, className }: Props) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const { data, loading } = useQuery(destinationCitiesQuery, {
    variables: { businessUnitId: activeBusinessUnitId! },
    skip: !activeBusinessUnitId,
  });

  const options = getCityOptions(
    removeNothings(data?.supplyChainLotDestinationCities?.edges?.map((edge) => edge?.node) ?? []),
  );

  return (
    <Combobox<string>
      id={id}
      name="destination-cities"
      options={options}
      selected={selected}
      onChange={onSelect}
      loading={loading}
      type="multi"
      className={className}
    />
  );
}

export const destinationCitiesQuery = graphql(`
  query SupplyChainLotDestinationCities($businessUnitId: ID!) {
    supplyChainLotDestinationCities(businessUnit: $businessUnitId) {
      edges {
        node {
          city
        }
      }
    }
  }
`);

export const getCityOptions = (cityNodes: CityNode[]) => {
  const uniqueCities = Array.from(new Set(cityNodes.map((node) => node.city.trim()).filter(Boolean))).map((city) => ({
    label: city,
    value: city,
  }));

  return uniqueCities;
};
